import { FieldType, FilterField, OutputFilterConfig } from './types';

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'];
const PREVIEW_MAX_LENGTH = 40;

export function detectFieldType(value: null | string | number | boolean | object): FieldType {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    const isImageUrl = IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext));
    const isImageContentType = lower.includes('content_type') && lower.includes('image');
    if (isImageUrl || isImageContentType) return 'image';
    return 'string';
  }
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') return 'object';
  return 'string';
}

export function generateValuePreview(value: null | string | number | boolean | object): string {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return String(value);
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    if (value.length > PREVIEW_MAX_LENGTH) return `"${value.substring(0, PREVIEW_MAX_LENGTH)}..."`;
    return `"${value}"`;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    const first = value[0];
    if (first !== null && typeof first === 'object' && !Array.isArray(first)) {
      const keys = Object.keys(first);
      const keysPreview = keys.length > 3 ? `${keys.slice(0, 3).join(', ')}, ...` : keys.join(', ');
      return `[ { ${keysPreview} } ]`;
    }
    const items = value.map((item) => {
      if (typeof item === 'string') return `"${item}"`;
      return String(item);
    });
    const joined = items.join(', ');
    if (joined.length > PREVIEW_MAX_LENGTH) return `[ ${joined.substring(0, PREVIEW_MAX_LENGTH)}... ]`;
    return `[ ${joined} ]`;
  }
  if (typeof value === 'object') {
    const keys = Object.keys(value);
    const keysPreview = keys.length > 3 ? `${keys.slice(0, 3).join(', ')}, ...` : keys.join(', ');
    return `{ ${keysPreview} }`;
  }
  return String(value);
}

function buildFieldTree(
  obj: Record<string, null | string | number | boolean | object>,
  parentPath: string,
): FilterField[] {
  return Object.entries(obj).map(([key, value]) => {
    const path = parentPath ? `${parentPath}.${key}` : key;
    const type = detectFieldType(value);
    const preview = generateValuePreview(value);
    const field: FilterField = { path, key, type, preview };
    if (type === 'object' && value !== null) {
      field.children = buildFieldTree(value as Record<string, null | string | number | boolean | object>, path);
    }
    if (type === 'array' && Array.isArray(value) && value.length > 0) {
      const first = value[0];
      if (first !== null && typeof first === 'object' && !Array.isArray(first)) {
        field.children = buildFieldTree(
          first as Record<string, null | string | number | boolean | object>,
          `${path}.0`,
        );
      }
    }
    return field;
  });
}

export function parseJsonToFields(json: string): FilterField[] {
  const parsed = JSON.parse(json);
  if (parsed === null || typeof parsed !== 'object') return [];
  if (Array.isArray(parsed)) {
    if (parsed.length === 0) return [];
    const first = parsed[0];
    if (first !== null && typeof first === 'object' && !Array.isArray(first)) {
      return buildFieldTree(first as Record<string, null | string | number | boolean | object>, '0');
    }
    return [];
  }
  return buildFieldTree(parsed as Record<string, null | string | number | boolean | object>, '');
}

function setNestedValue(
  obj: Record<string, unknown>,
  pathParts: string[],
  value: unknown,
): void {
  let current: Record<string, unknown> = obj;
  for (let i = 0; i < pathParts.length - 1; i++) {
    const part = pathParts[i];
    if (current[part] === undefined) {
      const nextPart = pathParts[i + 1];
      current[part] = /^\d+$/.test(nextPart) ? [] : {};
    }
    current = current[part] as Record<string, unknown>;
  }
  current[pathParts[pathParts.length - 1]] = value;
}

function getNestedValue(obj: Record<string, unknown>, pathParts: string[]): unknown {
  let current: unknown = obj;
  for (const part of pathParts) {
    if (current === null || current === undefined) return undefined;
    if (Array.isArray(current)) {
      const index = parseInt(part, 10);
      current = current[index];
    } else if (typeof current === 'object') {
      current = (current as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  return current;
}

function resolveLeafPaths(
  parsed: Record<string, unknown>,
  selectedPaths: string[],
): { path: string; parts: string[]; value: unknown }[] {
  const results: { path: string; parts: string[]; value: unknown }[] = [];
  for (const selectedPath of selectedPaths) {
    const parts = selectedPath.split('.');
    const value = getNestedValue(parsed, parts);
    if (value === undefined) continue;
    if (value !== null && typeof value === 'object') continue;
    results.push({ path: selectedPath, parts, value });
  }
  return results;
}

function expandArrayPaths(
  parsed: Record<string, unknown>,
  selectedPaths: string[],
): string[] {
  const expanded: string[] = [];
  for (const selectedPath of selectedPaths) {
    const parts = selectedPath.split('.');
    expandPathRecursive(parsed, parts, 0, [], expanded);
  }
  return [...new Set(expanded)];
}

function expandPathRecursive(
  current: unknown,
  parts: string[],
  index: number,
  builtParts: string[],
  results: string[],
): void {
  if (index >= parts.length) {
    results.push(builtParts.join('.'));
    return;
  }
  const part = parts[index];
  if (current === null || current === undefined) return;
  if (Array.isArray(current) && /^\d+$/.test(part)) {
    for (let i = 0; i < current.length; i++) {
      expandPathRecursive(current[i], parts, index + 1, [...builtParts, String(i)], results);
    }
    return;
  }
  if (Array.isArray(current)) return;
  if (typeof current === 'object') {
    const obj = current as Record<string, unknown>;
    if (obj[part] !== undefined) {
      expandPathRecursive(obj[part], parts, index + 1, [...builtParts, part], results);
    }
  }
}

export function applyFilter(json: string, config: OutputFilterConfig): string {
  const parsed = JSON.parse(json);
  if (config.selectedPaths.length === 0) return config.mode === 'structured' ? '{}' : '';
  const isArrayRoot = Array.isArray(parsed);
  const source = isArrayRoot ? parsed : parsed;
  if (config.mode === 'raw') {
    if (isArrayRoot) {
      const values: string[] = [];
      for (let i = 0; i < source.length; i++) {
        const itemPaths = config.selectedPaths.map((p) => {
          const parts = p.split('.');
          if (/^\d+$/.test(parts[0])) return [String(i), ...parts.slice(1)].join('.');
          return p;
        });
        const leafs = resolveLeafPaths({ root: source } as Record<string, unknown>, itemPaths.map((p) => `root.${p}`));
        for (const leaf of leafs) {
          values.push(String(leaf.value));
        }
      }
      return values.join('\n');
    }
    const allExpanded = expandArrayPaths(parsed, config.selectedPaths);
    const leafs = resolveLeafPaths(parsed, allExpanded);
    return leafs.map((l) => String(l.value)).join('\n');
  }
  if (isArrayRoot) {
    const resultArray: Record<string, unknown>[] = [];
    for (let i = 0; i < source.length; i++) {
      const item = source[i];
      if (item === null || typeof item !== 'object') continue;
      const mappedPaths = config.selectedPaths.map((p) => {
        const parts = p.split('.');
        if (/^\d+$/.test(parts[0])) return parts.slice(1).join('.');
        return p;
      }).filter((p) => p.length > 0);
      const expandedPaths = expandArrayPaths(item as Record<string, unknown>, mappedPaths);
      const leafPaths = resolveLeafPaths(item as Record<string, unknown>, expandedPaths);
      const resultItem: Record<string, unknown> = {};
      for (const leaf of leafPaths) {
        setNestedValue(resultItem, leaf.parts, leaf.value);
      }
      if (Object.keys(resultItem).length > 0) resultArray.push(resultItem);
    }
    return JSON.stringify(resultArray, null, 2);
  }
  const expandedPaths = expandArrayPaths(parsed, config.selectedPaths);
  const leafPaths = resolveLeafPaths(parsed, expandedPaths);
  const result: Record<string, unknown> = {};
  for (const leaf of leafPaths) {
    setNestedValue(result, leaf.parts, leaf.value);
  }
  return JSON.stringify(result, null, 2);
}

function extractKeysRecursive(value: unknown, keys: string[], prefix: string): void {
  if (value === null || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    keys.push(`${prefix}[]`);
    if (value.length > 0 && value[0] !== null && typeof value[0] === 'object') {
      extractKeysRecursive(value[0], keys, `${prefix}[]`);
    }
    return;
  }
  const obj = value as Record<string, unknown>;
  const sortedEntries = Object.keys(obj).sort();
  for (const key of sortedEntries) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    keys.push(fullKey);
    extractKeysRecursive(obj[key], keys, fullKey);
  }
}

export function computeSchemaHash(json: string): string {
  const parsed = JSON.parse(json);
  const keys: string[] = [];
  extractKeysRecursive(parsed, keys, '');
  keys.sort();
  return btoa(JSON.stringify(keys));
}

export function getAllPaths(fields: FilterField[]): string[] {
  const paths: string[] = [];
  for (const field of fields) {
    if (field.children && field.children.length > 0) {
      paths.push(...getAllPaths(field.children));
    } else {
      paths.push(field.path);
    }
  }
  return paths;
}

export function getChildPaths(field: FilterField): string[] {
  if (!field.children || field.children.length === 0) return [field.path];
  const paths: string[] = [];
  for (const child of field.children) {
    paths.push(...getChildPaths(child));
  }
  return paths;
}

export function getSelectedCount(
  fields: FilterField[],
  selectedPaths: string[],
): { selected: number; total: number } {
  const allLeafPaths = getAllPaths(fields);
  const selectedSet = new Set(selectedPaths);
  const selected = allLeafPaths.filter((p) => selectedSet.has(p)).length;
  return { selected, total: allLeafPaths.length };
}
